import { useAuthStore, refreshTokenStore, type IssuedSession } from '@/lib/auth'
import { useConnectivityStore } from '@/lib/connectivity'
import { HTTP_HEADERS } from '@/lib/constants'
import { API_BASE_URL } from '@/lib/env'

/** Metadatos de paginación de los listados (`?page=&pageSize=`). */
export interface PageMeta {
  page: number
  pageSize: number
  total: number
  totalPages: number
}

/** Respuesta paginada sin desempacar: `{ data, meta }`. */
export interface Paginated<T> {
  data: T[]
  meta: PageMeta
}

/** Parámetros de consulta aceptados; `undefined`/`null`/'' se omiten. */
export type QueryParams = Record<string, string | number | boolean | null | undefined>

/** Incidencia de validación de Zod tal como la reenvía el API en `details`. */
export interface ValidationIssue {
  path: (string | number)[]
  message: string
  code?: string
}

/** Código que usamos cuando no hubo respuesta del servidor. */
export const NETWORK_ERROR_CODE = 'network_error'
/** Estado HTTP que se asigna cuando no hubo respuesta (sin señal, DNS, CORS). */
export const NO_RESPONSE_STATUS = 0
/** Código del API para errores de validación (422 con incidencias de Zod). */
export const VALIDATION_ERROR_CODE = 'validation_failed'

/** Mensaje mostrado cuando no hay conexión con el servidor. */
const NETWORK_ERROR_MESSAGE = 'No hay conexión con el servidor. Revise su señal e intente de nuevo.'
/** Mensaje genérico cuando el servidor no envió un cuerpo de error legible. */
const UNKNOWN_ERROR_MESSAGE = 'Ocurrió un error inesperado. Intente de nuevo.'

const HTTP_NO_CONTENT = 204
const HTTP_UNAUTHORIZED = 401
const HTTP_FORBIDDEN = 403

/**
 * Error del API con la forma `{ success:false, error:{ code, message, details, requestId } }`.
 * `fieldErrors` traduce las incidencias de Zod (422) a `{ 'ruta.del.campo': 'mensaje' }`
 * para mostrarlas junto a cada campo del formulario.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly requestId: string | undefined
  readonly details: unknown
  readonly fieldErrors: Record<string, string>

  constructor(init: { status: number; code: string; message: string; requestId?: string | undefined; details?: unknown }) {
    super(init.message)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    this.requestId = init.requestId
    this.details = init.details
    this.fieldErrors = init.code === VALIDATION_ERROR_CODE ? issuesToFieldErrors(init.details) : {}
  }

  /** ¿El error ocurrió porque no hubo respuesta (sin señal, DNS, CORS)? */
  get isNetworkError(): boolean {
    return this.code === NETWORK_ERROR_CODE
  }

  /** ¿Es un error del cliente (4xx) que no tiene sentido reintentar? */
  get isClientError(): boolean {
    const CLIENT_ERROR_MIN = 400
    const SERVER_ERROR_MIN = 500
    return this.status >= CLIENT_ERROR_MIN && this.status < SERVER_ERROR_MIN
  }
}

/**
 * Convierte las incidencias de Zod en un mapa campo → primer mensaje.
 * Las incidencias sin ruta (reglas del objeto completo) quedan bajo `''`.
 */
export function issuesToFieldErrors(details: unknown): Record<string, string> {
  if (!Array.isArray(details)) return {}
  const result: Record<string, string> = {}
  for (const issue of details as Partial<ValidationIssue>[]) {
    if (typeof issue.message !== 'string') continue
    const key = Array.isArray(issue.path) ? issue.path.join('.') : ''
    result[key] ??= issue.message
  }
  return result
}

interface ErrorBody {
  error?: { code?: string; message?: string; details?: unknown; requestId?: string }
}

async function errorFromResponse(res: Response, fallbackRequestId: string): Promise<ApiError> {
  const body = (await res.json().catch(() => ({}))) as ErrorBody
  return new ApiError({
    status: res.status,
    code: body.error?.code ?? `http_${String(res.status)}`,
    message: body.error?.message ?? UNKNOWN_ERROR_MESSAGE,
    details: body.error?.details,
    requestId: body.error?.requestId ?? res.headers.get(HTTP_HEADERS.requestId) ?? fallbackRequestId,
  })
}

function networkError(requestId: string): ApiError {
  return new ApiError({ status: NO_RESPONSE_STATUS, code: NETWORK_ERROR_CODE, message: NETWORK_ERROR_MESSAGE, requestId })
}

/** Construye la URL absoluta del API con sus parámetros de consulta. */
export function buildApiUrl(path: string, params?: QueryParams): string {
  const url = new URL(API_BASE_URL + path, window.location.origin)
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null || value === '') continue
    url.searchParams.set(key, String(value))
  }
  return url.toString()
}

function newRequestId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

// ── Refresco de sesión: una sola petición a la vez, con rotación ────────────

/** Resultado de intentar renovar la sesión. */
export type RefreshOutcome = 'refreshed' | 'rejected' | 'offline'

/** Nombre del candado entre pestañas (Web Locks) para no rotar el token dos veces. */
const REFRESH_LOCK_NAME = 'tumin-session-refresh'

let refreshInFlight: Promise<RefreshOutcome> | null = null

/**
 * Renueva el token de acceso con el token de refresco. Es "single-flight":
 * las llamadas concurrentes comparten la misma promesa. El API ROTA el
 * token de refresco en cada uso (reutilizar uno viejo revoca la sesión), así
 * que se guarda el nuevo siempre y, entre pestañas, se serializa con Web
 * Locks releyendo el token más reciente dentro del candado.
 */
export function refreshSession(): Promise<RefreshOutcome> {
  refreshInFlight ??= withCrossTabLock(performRefresh).finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

async function withCrossTabLock<T>(task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LockManager }).locks : undefined
  if (!locks) return task()
  return locks.request(REFRESH_LOCK_NAME, task) as Promise<T>
}

async function performRefresh(): Promise<RefreshOutcome> {
  // Se lee dentro del candado: otra pestaña pudo haberlo rotado hace un instante.
  const refreshToken = refreshTokenStore.get()
  if (!refreshToken) return 'rejected'
  let res: Response
  try {
    res = await fetch(buildApiUrl('/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', [HTTP_HEADERS.requestId]: newRequestId() },
      body: JSON.stringify({ refreshToken }),
    })
  } catch {
    // Sin señal: se conserva la sesión para reintentar cuando vuelva la conexión.
    return 'offline'
  }
  if (!res.ok) {
    if (res.status === HTTP_UNAUTHORIZED || res.status === HTTP_FORBIDDEN) {
      useAuthStore.getState().clearSession()
      return 'rejected'
    }
    return 'offline'
  }
  const json = (await res.json()) as { data: IssuedSession }
  useAuthStore.getState().setSession(json.data)
  return 'refreshed'
}

/**
 * Garantiza un token de acceso en memoria (p. ej. tras recargar la página).
 * Devuelve `true` si hay sesión utilizable.
 */
export async function ensureSession(): Promise<boolean> {
  if (useAuthStore.getState().accessToken) return true
  if (!refreshTokenStore.get()) return false
  const outcome = await refreshSession()
  return outcome === 'refreshed'
}

// ── Petición genérica ───────────────────────────────────────────────────────

/** Opciones de una petición al API. */
export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  params?: QueryParams | undefined
  /** Cuerpo JSON o `FormData` (subida de archivos). */
  body?: unknown
  headers?: Record<string, string>
  signal?: AbortSignal | undefined
  /** `false` para endpoints públicos (no envía token ni intenta refrescar). */
  auth?: boolean
}

async function send(path: string, options: RequestOptions, isRetry = false): Promise<Response> {
  const { method = 'GET', params, body, headers: extraHeaders, signal, auth = true } = options
  const store = useAuthStore.getState()
  if (auth && !store.accessToken && !isRetry && refreshTokenStore.get()) await refreshSession()

  const tokenUsed = auth ? useAuthStore.getState().accessToken : null
  const requestId = newRequestId()
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData
  const headers: Record<string, string> = { [HTTP_HEADERS.requestId]: requestId, ...extraHeaders }
  if (body !== undefined && !isFormData) headers['Content-Type'] = 'application/json'
  if (tokenUsed) headers.Authorization = `Bearer ${tokenUsed}`

  let res: Response
  try {
    res = await fetch(buildApiUrl(path, params), {
      method,
      headers,
      ...(signal ? { signal } : {}),
      ...(body !== undefined ? { body: isFormData ? (body as FormData) : JSON.stringify(body) } : {}),
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw networkError(requestId)
  }

  if (res.status === HTTP_UNAUTHORIZED && auth && !isRetry) {
    // Otra petición ya renovó el token mientras esta viajaba: basta reintentar.
    const current = useAuthStore.getState().accessToken
    if (current && current !== tokenUsed) return send(path, options, true)
    const outcome = await refreshSession()
    if (outcome === 'refreshed') return send(path, options, true)
    if (outcome === 'offline') throw networkError(requestId)
  }

  if (!res.ok) throw await errorFromResponse(res, requestId)

  if (method === 'GET') {
    const cachedAt = res.headers.get(HTTP_HEADERS.cachedAt)
    if (cachedAt) useConnectivityStore.getState().markStale(Number(cachedAt))
    else useConnectivityStore.getState().markFresh()
  }
  return res
}

async function readJson(res: Response): Promise<unknown> {
  if (res.status === HTTP_NO_CONTENT) return undefined
  const text = await res.text()
  return text ? (JSON.parse(text) as unknown) : undefined
}

/** Ejecuta una petición y desempaca `data` de `{ success, data }`. */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const json = (await readJson(await send(path, options))) as { data?: T } | undefined
  return json?.data as T
}

/** Archivo descargado del API. */
export interface DownloadedFile {
  blob: Blob
  /** Nombre sugerido por `Content-Disposition`, si el API lo envió. */
  filename: string | null
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (utf8?.[1]) return decodeURIComponent(utf8[1])
  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain?.[1] ?? null
}

/** Ofrece un Blob al usuario como descarga con el nombre indicado. */
export function saveBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(objectUrl)
}

/**
 * Cliente del API. Todas las funciones lanzan `ApiError` ante errores.
 * - `get`/`post`/`patch`/`put`/`delete` desempacan `data`.
 * - `list` devuelve `{ data, meta }` de los listados paginados.
 * - `upload` envía `FormData` (multipart).
 * - `download` devuelve el Blob; `downloadAndSave` además lo guarda.
 */
export const api = {
  get: <T>(path: string, params?: QueryParams, signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', params, signal }),

  list: async <T>(path: string, params?: QueryParams, signal?: AbortSignal): Promise<Paginated<T>> => {
    const json = (await readJson(await send(path, { method: 'GET', params, signal }))) as Paginated<T>
    return { data: json.data, meta: json.meta }
  },

  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),

  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),

  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),

  delete: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),

  upload: <T>(path: string, form: FormData, method: 'POST' | 'PUT' = 'POST') => request<T>(path, { method, body: form }),

  download: async (path: string, params?: QueryParams): Promise<DownloadedFile> => {
    const res = await send(path, { method: 'GET', params })
    return { blob: await res.blob(), filename: filenameFromDisposition(res.headers.get('Content-Disposition')) }
  },

  downloadAndSave: async (path: string, fallbackName: string, params?: QueryParams): Promise<void> => {
    const file = await api.download(path, params)
    saveBlob(file.blob, file.filename ?? fallbackName)
  },

  /** Endpoints sin sesión (`/public/*`). */
  public: {
    get: <T>(path: string, params?: QueryParams) => request<T>(path, { method: 'GET', params, auth: false }),
    post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body, auth: false }),
  },
}
